/**
 * @file routes/_app/risk-assessment/annual.tsx
 * @description 年度客戶風險評估表（ZHQP27 附表4「危害鑑別與風險評估表」，客戶 PPT S25/S26）。
 *
 * 貼近客戶 Excel 樣式的表格化評估：
 * - 行 = 客戶（每客戶每年度一筆）；列 = 8 個評估項分數 + 嚴重度 + 小計/合計 + 風險等級
 *   + B 區（管制措施及因應對策/控管情形）+ 備註/評估者
 * - A 區（8 項評分 + 評分說明）由 AI 自動判定（建立時預填），可在 Drawer 逐項人工修正；
 *   B 區由管理組在 Drawer 填寫
 * - 「生成本年度評估」批量為缺失客戶補建（上年度有評估則逐年遞減 3→2→1→0 預填）
 * - 等級色塊：低（綠）/ 中（橙）/ 高（紅）；opLog 以時間線展示於 Drawer
 *
 * 權限：讀 analytics.read；寫（生成/修正）case.dispatch —— 按鈕依 userCan() 條件渲染。
 * 舊 /risk-assessment 概觀頁保留，本頁與其以頁內入口並列（互設跳轉按鈕）。
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Table,
  Tag,
  Timeline,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Info, Sparkles } from 'lucide-react';
import dayjs from 'dayjs';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { PageShell, SectionCard } from '@/components/shell';
import { useAuthStore } from '@/lib/auth-context';
import {
  RISK_ANNUAL_ITEM_DEFS,
  RISK_ANNUAL_LEVEL_HINTS,
  RISK_SEVERITY_OPTIONS,
  calcRiskLevel,
  calcTotals,
  userCan,
  type RiskAnnualDTO,
  type RiskAnnualItemKey,
  type RiskAnnualLevel,
} from '@zhcb/shared';
import { color, space, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/risk-assessment/annual')({
  component: RiskAnnualPage,
});

/* ════════════════════════════════════════════════
 * 常量與工具
 * ════════════════════════════════════════════════ */

/** 分頁回應 */
interface PaginatedResponse {
  items: RiskAnnualDTO[];
  total: number;
  page: number;
  pageSize: number;
}

/** 風險等級 → antd Tag 色 */
const LEVEL_COLOR: Record<RiskAnnualLevel, string> = {
  低: 'green',
  中: 'orange',
  高: 'red',
};

/** Drawer 編輯草稿（items 按 key 打平便於受控輸入） */
interface EditDraft {
  scores: Record<RiskAnnualItemKey, number>;
  scoreNotes: Record<RiskAnnualItemKey, string>;
  severity: number;
  controlMeasures: string;
  controlStatus: string;
  notes: string;
}

/** 由 DTO 生成編輯草稿 */
function toDraft(row: RiskAnnualDTO): EditDraft {
  const scores = {} as Record<RiskAnnualItemKey, number>;
  const scoreNotes = {} as Record<RiskAnnualItemKey, string>;
  for (const def of RISK_ANNUAL_ITEM_DEFS) {
    const item = row.items.find((it) => it.key === def.key);
    scores[def.key] = item?.score ?? 0;
    scoreNotes[def.key] = item?.scoreNote ?? '';
  }
  return {
    scores,
    scoreNotes,
    severity: row.severity,
    controlMeasures: row.controlMeasures ?? '',
    controlStatus: row.controlStatus ?? '',
    notes: row.notes ?? '',
  };
}

/* ════════════════════════════════════════════════
 * 頁面組件
 * ════════════════════════════════════════════════ */

function RiskAnnualPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  /** 是否具寫入權（生成/修正；讀寫分離：讀 analytics.read 由後端保證） */
  const canWrite = user ? userCan(user, 'case.dispatch') : false;

  /* ---- 篩選狀態 ---- */
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  /* ---- 列表查詢 ---- */
  const listQuery = useQuery({
    queryKey: ['risk-annual', year, search, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: '50' });
      params.set('year', String(year));
      if (search.trim()) params.set('search', search.trim());
      return apiClient.get<PaginatedResponse>(`/api/risk-annual?${params.toString()}`);
    },
  });

  /* ---- 批量生成本年度評估 ---- */
  const batchMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ created: number; skipped: number }>('/api/risk-annual/batch', { year }),
    onSuccess: (data) => {
      message.success(`已生成 ${data.created} 筆，跳過已存在 ${data.skipped} 筆`);
      void queryClient.invalidateQueries({ queryKey: ['risk-annual'] });
    },
    onError: (err) => {
      console.error('[RiskAnnual] batch create failed:', err);
      message.error('生成年度評估失敗');
    },
  });

  /* ---- Drawer 編輯 ---- */
  const [editing, setEditing] = useState<RiskAnnualDTO | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);

  const openEdit = (row: RiskAnnualDTO) => {
    setEditing(row);
    setDraft(toDraft(row));
  };
  const closeEdit = () => {
    setEditing(null);
    setDraft(null);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: { id: number; body: unknown }) =>
      apiClient.patch<{ item: RiskAnnualDTO }>(`/api/risk-annual/${payload.id}`, payload.body),
    onSuccess: () => {
      message.success('評估已保存');
      void queryClient.invalidateQueries({ queryKey: ['risk-annual'] });
      closeEdit();
    },
    onError: (err) => {
      console.error('[RiskAnnual] save failed:', err);
      message.error('保存失敗');
    },
  });

  const handleSave = () => {
    if (!editing || !draft) return;
    saveMutation.mutate({
      id: editing.id,
      body: {
        items: RISK_ANNUAL_ITEM_DEFS.map((def) => ({
          key: def.key,
          score: draft.scores[def.key],
          scoreNote: draft.scoreNotes[def.key] || null,
        })),
        severity: draft.severity,
        controlMeasures: draft.controlMeasures || null,
        controlStatus: draft.controlStatus || null,
        notes: draft.notes || null,
      },
    });
  };

  /** Drawer 內即時預覽小計/合計/等級（與後端同一套純函數） */
  const preview = useMemo(() => {
    if (!draft) return null;
    const items = RISK_ANNUAL_ITEM_DEFS.map((def) => ({ score: draft.scores[def.key] }));
    const { subtotal, total } = calcTotals(items, draft.severity);
    return { subtotal, total, level: calcRiskLevel(total) };
  }, [draft]);

  /* ---- 表格列定義（貼近 Excel 樣式） ---- */
  const columns: ColumnsType<RiskAnnualDTO> = useMemo(() => {
    const itemColumns: ColumnsType<RiskAnnualDTO> = RISK_ANNUAL_ITEM_DEFS.map((def) => ({
      title: (
        <Tooltip title={`${def.label}：${def.guide}`}>
          <span style={{ cursor: 'help' }}>{def.no}</span>
        </Tooltip>
      ),
      key: def.key,
      width: 52,
      align: 'center' as const,
      render: (_: unknown, row: RiskAnnualDTO) => {
        const item = row.items.find((it) => it.key === def.key);
        if (!item) return '—';
        return (
          <Tooltip title={item.scoreNote || def.label}>
            <span
              style={{
                fontWeight: weight.semibold,
                color:
                  item.score >= 3
                    ? color.state.danger
                    : item.score >= 2
                      ? color.state.warning
                      : undefined,
              }}
            >
              {item.score}
            </span>
          </Tooltip>
        );
      },
    }));

    return [
      {
        title: '序號',
        key: 'index',
        width: 56,
        align: 'center',
        render: (_: unknown, __: RiskAnnualDTO, idx: number) => (page - 1) * 50 + idx + 1,
      },
      {
        title: '評估日期',
        dataIndex: 'assessedDate',
        key: 'assessedDate',
        width: 108,
        render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
      },
      {
        title: '註冊號',
        dataIndex: 'clientCode',
        key: 'clientCode',
        width: 130,
        render: (v: string | null) => v ?? '—',
      },
      {
        title: '客戶名稱',
        dataIndex: 'clientName',
        key: 'clientName',
        width: 180,
        ellipsis: true,
        render: (v: string | null) => v ?? '—',
      },
      ...itemColumns,
      {
        title: '嚴重度',
        dataIndex: 'severity',
        key: 'severity',
        width: 72,
        align: 'center',
      },
      {
        title: '小計',
        dataIndex: 'subtotal',
        key: 'subtotal',
        width: 64,
        align: 'center',
      },
      {
        title: '合計',
        dataIndex: 'total',
        key: 'total',
        width: 64,
        align: 'center',
        render: (v: number) => <span style={{ fontWeight: weight.semibold }}>{v}</span>,
      },
      {
        title: '風險等級',
        dataIndex: 'level',
        key: 'level',
        width: 92,
        align: 'center',
        render: (v: RiskAnnualLevel) => (
          <Tooltip title={RISK_ANNUAL_LEVEL_HINTS[v]}>
            <Tag color={LEVEL_COLOR[v]}>{v}</Tag>
          </Tooltip>
        ),
      },
      {
        title: '管制措施及因應對策',
        dataIndex: 'controlMeasures',
        key: 'controlMeasures',
        width: 200,
        ellipsis: true,
        render: (v: string | null) => v || '—',
      },
      {
        title: '控管情形',
        dataIndex: 'controlStatus',
        key: 'controlStatus',
        width: 160,
        ellipsis: true,
        render: (v: string | null) => v || '—',
      },
      {
        title: '備註',
        dataIndex: 'notes',
        key: 'notes',
        width: 140,
        ellipsis: true,
        render: (v: string | null) => v || '—',
      },
      {
        title: '評估者',
        dataIndex: 'assessorName',
        key: 'assessorName',
        width: 90,
        render: (v: string | null) => v ?? '—',
      },
      {
        title: '操作',
        key: 'actions',
        width: 72,
        fixed: 'right',
        render: (_: unknown, row: RiskAnnualDTO) => (
          <Button type="link" size="small" onClick={() => openEdit(row)}>
            {canWrite ? '編輯' : '查看'}
          </Button>
        ),
      },
    ];
  }, [page, canWrite]);

  /* ---- 渲染 ---- */
  return (
    <PageShell
      title="年度風險評估表"
      description="ZHQP27 附表4「危害鑑別與風險評估表」：每客戶每年度一筆，8 個評估項由 AI 自動判定（可人工修正），管制措施由管理組填寫；風險等級 低（<20）/ 中（20-40）/ 高（>40）。"
      actions={
        <div style={{ display: 'flex', gap: space[2] }}>
          <Link to="/risk-assessment">
            <Button icon={<ClipboardList size={14} />}>風險矩陣概觀</Button>
          </Link>
          {canWrite && (
            <Popconfirm
              title={`為 ${year} 年度尚無評估的啟用客戶批量生成？`}
              description="上年度有評估者按逐年遞減預填，否則由 AI 依歷史數據評分。"
              onConfirm={() => batchMutation.mutate()}
              okText="生成"
              cancelText="取消"
            >
              <Button
                type="primary"
                icon={<Sparkles size={14} />}
                loading={batchMutation.isPending}
              >
                生成本年度評估
              </Button>
            </Popconfirm>
          )}
        </div>
      }
    >
      <SectionCard
        icon={<Info size={14} />}
        title={`${year} 年度危害鑑別與風險評估`}
        description="①前次查驗NC ②禁用物質 ③區域地理 ④產品 ⑤標章標示 ⑥複雜度 ⑦市售違規 ⑧整體表現（各 0-3 分；懸停欄頭查看評分說明）"
        padless
      >
        {/* 篩選列 */}
        <div
          style={{
            display: 'flex',
            gap: space[3],
            padding: `${space[3]}px ${space[5]}px`,
            borderBottom: `1px solid ${color.border.subtle}`,
            background: color.surface.muted,
          }}
        >
          <Select
            value={year}
            onChange={(v) => {
              setYear(v);
              setPage(1);
            }}
            options={Array.from({ length: 6 }, (_, i) => currentYear + 1 - i).map((y) => ({
              label: `${y} 年度`,
              value: y,
            }))}
            style={{ width: 120 }}
          />
          <Input.Search
            placeholder="搜尋客戶名稱 / 註冊號..."
            allowClear
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
            style={{ width: 260 }}
          />
        </div>

        <Table<RiskAnnualDTO>
          rowKey="id"
          size="small"
          loading={listQuery.isLoading}
          dataSource={listQuery.data?.items ?? []}
          columns={columns}
          scroll={{ x: 1700 }}
          pagination={{
            current: page,
            pageSize: 50,
            total: listQuery.data?.total ?? 0,
            showTotal: (t) => `共 ${t} 筆`,
            onChange: (p) => setPage(p),
          }}
        />
      </SectionCard>

      {/* ===== 編輯 Drawer（A 區逐項修正 + B 區填寫 + 操作日誌） ===== */}
      <Drawer
        title={
          editing
            ? `${editing.year} 年度風險評估 — ${editing.clientName ?? ''}（${editing.clientCode ?? ''}）`
            : ''
        }
        size="large"
        open={editing != null}
        onClose={closeEdit}
        footer={
          canWrite && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space[2] }}>
              <Button onClick={closeEdit}>取消</Button>
              <Button type="primary" loading={saveMutation.isPending} onClick={handleSave}>
                保存
              </Button>
            </div>
          )
        }
      >
        {editing && draft && preview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space[5] }}>
            {/* 即時合計預覽 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: space[4],
                padding: space[4],
                background: color.surface.muted,
                border: `1px solid ${color.border.subtle}`,
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
                小計 <b style={{ color: color.text.primary }}>{preview.subtotal}</b>
              </span>
              <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
                × 嚴重度 <b style={{ color: color.text.primary }}>{draft.severity}</b>
              </span>
              <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
                = 合計 <b style={{ color: color.text.primary }}>{preview.total}</b>
              </span>
              <Tooltip title={RISK_ANNUAL_LEVEL_HINTS[preview.level]}>
                <Tag color={LEVEL_COLOR[preview.level]}>{preview.level}風險</Tag>
              </Tooltip>
            </div>

            {/* A 區：8 項評分 */}
            <SectionCard
              title="A 區 · 評估項評分"
              description="AI 自動判定，可人工修正（修正後來源標記為「人工」）"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: space[4] }}>
                {RISK_ANNUAL_ITEM_DEFS.map((def) => {
                  const item = editing.items.find((it) => it.key === def.key);
                  return (
                    <div key={def.key}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: space[2],
                          marginBottom: 4,
                        }}
                      >
                        <span style={{ fontSize: text.md, fontWeight: weight.medium }}>
                          {def.no} {def.label}
                        </span>
                        <Tag color={item?.source === 'manual' ? 'blue' : 'purple'}>
                          {item?.source === 'manual' ? '人工' : 'AI'}
                        </Tag>
                        <Tooltip title={def.guide}>
                          <Info size={13} color={color.text.muted} style={{ cursor: 'help' }} />
                        </Tooltip>
                      </div>
                      <div style={{ display: 'flex', gap: space[2] }}>
                        <InputNumber
                          min={0}
                          max={3}
                          value={draft.scores[def.key]}
                          disabled={!canWrite}
                          onChange={(v) =>
                            setDraft({
                              ...draft,
                              scores: { ...draft.scores, [def.key]: v ?? 0 },
                            })
                          }
                          style={{ width: 72 }}
                        />
                        <Input
                          placeholder="評分說明"
                          value={draft.scoreNotes[def.key]}
                          disabled={!canWrite}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              scoreNotes: { ...draft.scoreNotes, [def.key]: e.target.value },
                            })
                          }
                        />
                      </div>
                    </div>
                  );
                })}

                {/* 嚴重度 */}
                <div>
                  <div style={{ fontSize: text.md, fontWeight: weight.medium, marginBottom: 4 }}>
                    嚴重度（權重）
                  </div>
                  <Select
                    value={draft.severity}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, severity: v })}
                    options={RISK_SEVERITY_OPTIONS.map((s) => ({ label: String(s), value: s }))}
                    style={{ width: 120 }}
                  />
                </div>
              </div>
            </SectionCard>

            {/* B 區：管理組填寫 */}
            <SectionCard title="B 區 · 管制風險評估" description="由管理組填寫">
              <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
                <div>
                  <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: 4 }}>
                    管制措施及因應對策
                  </div>
                  <Input.TextArea
                    rows={3}
                    value={draft.controlMeasures}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, controlMeasures: e.target.value })}
                  />
                </div>
                <div>
                  <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: 4 }}>
                    控管情形
                  </div>
                  <Input.TextArea
                    rows={3}
                    value={draft.controlStatus}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, controlStatus: e.target.value })}
                  />
                </div>
                <div>
                  <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: 4 }}>
                    備註
                  </div>
                  <Input.TextArea
                    rows={2}
                    value={draft.notes}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  />
                </div>
              </div>
            </SectionCard>

            {/* 操作日誌時間線 */}
            <SectionCard title="操作日誌" description={`共 ${editing.opLog.length} 條`}>
              {editing.opLog.length === 0 ? (
                <span style={{ color: color.text.muted, fontSize: text.sm }}>暫無記錄</span>
              ) : (
                <Timeline
                  items={[...editing.opLog].reverse().map((entry) => ({
                    children: (
                      <div>
                        <div style={{ fontSize: text.sm, color: color.text.secondary }}>
                          {entry.detail ?? entry.action}
                        </div>
                        <div style={{ fontSize: text.xs, color: color.text.muted }}>
                          {dayjs(entry.at).format('YYYY-MM-DD HH:mm')} · {entry.byName}
                        </div>
                      </div>
                    ),
                  }))}
                />
              )}
            </SectionCard>
          </div>
        )}
      </Drawer>
    </PageShell>
  );
}
